import b from 'b_';
import cx from 'classnames';
import { isSameWeek } from 'date-fns';
import { ArrowShortBold, Calendar } from 'education-icons';
import { Button, H4, SimpleCallback } from 'platform-components';
import { TEXT_WEIGHT } from 'platform-components/src/components/constants';
import { PopupBase, withTargetAnchor, withZIndex } from 'platform-components/src/components/ui/Popup/Popup';
import { compose } from 'platform-components/src/utils/bem-react-core';
import { withOutsideClick } from 'platform-components/src/utils/hocs/withOutsideClick';
import React, { useRef, useState } from 'react';
import { useMedia } from 'react-use';

import { MarkedDay, WeekPicker } from 'common.components/week-picker/week-picker';
import { formatInterval, INTERVAL_FORMAT } from 'utils/date/format-interval';

import './calendar-controls.scss';

const Popup = compose(withOutsideClick, withZIndex, withTargetAnchor)(PopupBase);

const MEDIA_QUERY_MOBILE = '(max-width: 599px)';
const MEDIA_QUERY_DESKTOP = '(min-width: 1280px)';

interface Props {
    startDate: Date;
    endDate: Date;
    onPrevButtonClick: SimpleCallback;
    onNextButtonClick: SimpleCallback;
    onChangeWeek: (start: Date, end: Date) => void;
    onChangeMonth?: (month: Date) => void;
    onOpenPopup?: SimpleCallback;
    onClosePopup?: SimpleCallback;
    markedDays?: MarkedDay[];
    isLoading?: boolean;
    mix?: string;
    minDate?: Date;
}
// eslint-disable-next-line complexity
export const CourseCalendarControls = (props: Props) => {
    const {
        startDate,
        endDate,
        onChangeMonth,
        onPrevButtonClick,
        markedDays,
        onNextButtonClick,
        onChangeWeek,
        mix,
        isLoading,
        onOpenPopup,
        onClosePopup,
        minDate,
    } = props;
    const isMobile = useMedia(MEDIA_QUERY_MOBILE);
    const isDesktop = useMedia(MEDIA_QUERY_DESKTOP);
    const [shouldShowPopup, setPopupVisibility] = useState(false);
    const owner = useRef(null);
    const disablePrevButton = minDate && isSameWeek(startDate, minDate);

    const handleCalendarClick = () => {
        changePopupVisibility(!shouldShowPopup);
    };

    const handleChangeWeek = (start: Date, end: Date) => {
        changePopupVisibility(false);
        onChangeWeek(start, end);
    };

    const changePopupVisibility = (isVisible: boolean) => {
        setPopupVisibility(isVisible);

        if (isVisible && onOpenPopup) {
            onOpenPopup();
        } else if (!isVisible && onClosePopup) {
            onClosePopup();
        }
    };

    return (
        <div className={cx(mix, 'calendar-controls')}>
            <Button
                className="calendar-controls__prev-button"
                disabled={disablePrevButton}
                icon={<ArrowShortBold />}
                onClick={onPrevButtonClick}
                size={isMobile ? Button.SIZE.L : Button.SIZE.M}
                view={Button.VIEW.RAISED}
            />
            {isDesktop && (
                <div className={b('calendar-controls', 'current', { desktop: true })}>
                    <Button
                        icon={<Calendar />}
                        innerRef={owner}
                        onClick={handleCalendarClick}
                        view={Button.VIEW.RAISED}
                    />
                    <H4 weight={TEXT_WEIGHT.M}>{formatInterval(startDate, endDate, INTERVAL_FORMAT.LONG)}</H4>
                </div>
            )}
            {!isDesktop && (
                <div className={b('calendar-controls', 'current', { mobile: true })}>
                    <Button
                        icon={<Calendar />}
                        innerRef={owner}
                        onClick={handleCalendarClick}
                        size={isMobile ? Button.SIZE.L : Button.SIZE.M}
                        view={Button.VIEW.RAISED}
                    >
                        {formatInterval(startDate, endDate, isMobile ? INTERVAL_FORMAT.SHORT : INTERVAL_FORMAT.MEDIUM)}
                    </Button>
                </div>
            )}
            <Button
                className="calendar-controls__next-button"
                icon={<ArrowShortBold />}
                onClick={onNextButtonClick}
                size={isMobile ? Button.SIZE.L : Button.SIZE.M}
                view={Button.VIEW.RAISED}
            />
            {shouldShowPopup && (
                <Popup
                    anchor={owner}
                    direction={['bottom-start', 'bottom', 'top-start']}
                    mainOffset={8}
                    onOutsideClick={() => changePopupVisibility(false)}
                    target="anchor"
                    visible={shouldShowPopup}
                >
                    <WeekPicker
                        endDate={endDate}
                        markedDays={markedDays}
                        minDate={new Date(2010, 0, 1)}
                        onChangeMonth={onChangeMonth}
                        onChangeWeek={handleChangeWeek}
                        progress={isLoading}
                        startDate={startDate}
                    />
                </Popup>
            )}
        </div>
    );
};
